'use client';

import { Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { type FormEvent, useRef, useState } from 'react';
import { useMyLook } from '@/lib/api/auth/hooks';
import { useCreateComment } from '@/lib/api/comments';
import { useAuthSession } from '@/lib/hooks/useAuth';
import { Textarea } from '@/lib/ui-kit/textarea';
import { cn } from '@/lib/ui-kit/utils';
import { Button } from '@/lib/ui-kit-v2/button';
import { UserAvatar } from '@/lib/ui-kit-v2/user-avatar';
import { useCommentsContext } from './CommentsContext';
import { CommentsFilters } from './CommentsFilters';
import { MAX_COMMENT_LENGTH } from './constants';
import { EmojiGifPicker } from './EmojiGifPicker';
import { buildGifMarker } from './gif';

export const CommentForm = () => {
  const { source } = useCommentsContext();
  const namespace = source.kind === 'event' ? 'eventComments' : 'comments';
  const t = useTranslations(namespace);
  const { user, isAuthenticated } = useAuthSession();
  const look = useMyLook();
  const createComment = useCreateComment(source);

  const [content, setContent] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  // Last known caret position in the textarea, so emoji land where the cursor
  // is even though clicking the picker doesn't move DOM focus.
  const caretRef = useRef<number | null>(null);

  const isSubmitting = createComment.isPending;
  const isDisabled = !isAuthenticated || isSubmitting;
  const hasContent = content.trim().length > 0;
  const charCount = content.length;
  const isOverLimit = charCount > MAX_COMMENT_LENGTH;
  const showCounter = charCount >= MAX_COMMENT_LENGTH * 0.9;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!hasContent || isOverLimit) return;
    createComment.mutate(
      { content: content.trim() },
      {
        onSuccess: () => {
          setContent('');
          setIsFocused(false);
        },
      },
    );
  };

  const handleCancel = () => {
    setContent('');
    setIsFocused(false);
  };

  const handleSelectEmoji = (emoji: string) => {
    setContent((prev) => {
      const pos = caretRef.current ?? prev.length;
      const next = prev.slice(0, pos) + emoji + prev.slice(pos);
      caretRef.current = pos + emoji.length;
      return next;
    });
  };

  const handleSelectGif = (id: string) => {
    if (isSubmitting) return;
    createComment.mutate(
      { content: buildGifMarker(id) },
      {
        onSuccess: () => {
          setContent('');
          setIsFocused(false);
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <div className="flex items-start gap-4">
          <UserAvatar userId={user?.id} name={user?.name} look={look} size={40} />

          <div
            className={cn(
              'flex min-w-0 flex-1 items-end gap-2 rounded-[10px] border px-4 py-3 transition-colors',
              isOverLimit
                ? 'border-destructive'
                : isFocused
                  ? 'border-v2-text-primary'
                  : 'border-v2-text-secondary',
            )}
          >
            <Textarea
              placeholder={isAuthenticated ? t('yourMessage') : t('signInToComment')}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onSelect={(e) => {
                caretRef.current = e.currentTarget.selectionStart;
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              disabled={isDisabled}
              className={cn(
                'box-border max-h-[200px]! min-h-6! w-full min-w-0 flex-1 rounded-none! border-0! bg-transparent! p-0!',
                'text-[13px]! leading-[150%]! text-v2-text-primary! outline-none! ring-0! wrap-anywhere',
                'placeholder:text-v2-text-secondary! focus:outline-none! focus:ring-0!',
                'disabled:cursor-not-allowed disabled:opacity-50',
              )}
            />
            <EmojiGifPicker
              onSelectEmoji={handleSelectEmoji}
              onSelectGif={handleSelectGif}
              disabled={isDisabled}
            />
          </div>
        </div>

        {(isOverLimit || showCounter) && (
          <div className="flex items-center justify-between gap-2 pl-14 text-[12px] leading-4">
            <span className={cn('text-destructive', !isOverLimit && 'invisible')}>
              {t('commentTooLong', { max: MAX_COMMENT_LENGTH })}
            </span>
            <span className={isOverLimit ? 'text-destructive' : 'text-v2-text-secondary'}>
              {charCount}/{MAX_COMMENT_LENGTH}
            </span>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:pl-14">
        <CommentsFilters />
        <div className="flex items-center justify-end gap-2">
          {hasContent && (
            <Button
              type="button"
              appearance="ghost"
              size="M"
              onClick={handleCancel}
              disabled={isSubmitting}
            >
              {t('cancel')}
            </Button>
          )}
          <Button
            type="submit"
            appearance="accent"
            size="M"
            disabled={isDisabled || !hasContent || isOverLimit}
          >
            {isSubmitting ? <Loader2 className="size-4 animate-spin" /> : t('send')}
          </Button>
        </div>
      </div>
    </form>
  );
};
